/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    Toolbar,
    TOOLBAR_ITEM_TYPE,
    toolbarItemDecorators,
    useGridApi,
    useModal,
} from '@veeam-vspc/components';

import type { ToolbarItems } from '@veeam-vspc/components';

import { useLang } from 'views/providers/LangProvider/hooks';
import addIcon from 'images/actions/add.svg';
import removeIcon from 'images/actions/remove.svg';
import editIcon from 'images/actions/edit.svg';
import { VcdResourceDialog } from '../VcdResourceDialog';

import type { VcdReplicaResource } from '../../../../../../interfaces';

interface Props {
    items: VcdReplicaResource[];
    cloudConnectAgentUid: string;
    organizationUid: string;
}

export const ActionsToolbar = ({ items, cloudConnectAgentUid, organizationUid }: Props) => {
    const lang = useLang();
    const gridApi = useGridApi<VcdReplicaResource, void, void>();

    const [quotaDialog, quotaDialogActions] = useModal<VcdReplicaResource>({
        render: ({ deactivate, data }) => (
            <VcdResourceDialog
                data={data}
                existingItems={items}
                cloudConnectAgentUid={cloudConnectAgentUid}
                organizationUid={organizationUid}
                onConfirm={(newData) => {
                    if (data) {
                        items[items.findIndex(item => item.dataCenterUid === data.dataCenterUid)] = newData;
                    } else {
                        items.push(newData);
                    }
                    gridApi.reloadGrid();
                }}
                deactivate={deactivate}
            />
        ),
    });

    const toolbarItems: ToolbarItems = [
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: addIcon,
            text: lang.ADD,
            onClick: () => {
                quotaDialogActions.activate();
            },
            decorators: [
            ],
        },
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: editIcon,
            text: lang.EDIT,
            onClick: async() => {
                const selected = gridApi.selected[0];
                quotaDialogActions.activate(selected);
            },
            decorators: [
                toolbarItemDecorators.disallowWithoutSingleSelection(),
            ],
        },
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: removeIcon,
            text: lang.REMOVE,
            onClick: async() => {
                const selected = gridApi.selected[0];
                items.splice(items.findIndex(item => item.dataCenterUid === selected.dataCenterUid), 1);
                gridApi.reloadGrid();
            },
            decorators: [
                toolbarItemDecorators.disallowWithoutSingleSelection(),
            ],
        },
    ];

    return (
        <>
            <Toolbar items={toolbarItems} />
            {quotaDialog}
        </>
    );
};
